import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { ColumnsType } from "antd/es/table";
import { PencilIcon, PlusIcon, TrashIcon, CodeBracketIcon } from '@heroicons/react/24/outline';
import Table from "../../lists/Table/Table";
import { CREATE_PLACEMENT, GET_ALL_WEBSITES_AND_TEMPLATES, GET_PLACEMENTS, UPDATE_PLACEMENT } from "../../../utils/api/placement";
import FormModal from "../../modals/FormModal";
import GetTagsModal from "../../modals/GetTagsModal";
import {useModal, useQueryVariables} from '../../../utils/hooks';
import SingleLineField from "../SingleLineField";
import DropdownField, { BasicDropdownField } from "../DropdownField";
import HiddenInput from "../HiddenInput";
import { Placement, CreatePlacementInput } from '../../../__generated__/graphql';
import ActionButton, { ActionHandler } from '../../lists/Table/shared/ActionButton';
import { useCurrentUser } from "../../../utils/CurrentUserContext";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import InputNumber from "../InputNumber";
import FormItem from '../utils/FormItem';

const PlacementFormContent: React.FC<{ optionsData: any }> = ({ optionsData }) => {
  const { watch, control } = useFormContext();
  const driverTemplateId = watch('driverTemplateId');
  const selectedTemplate = optionsData?.templates?.items?.find((t: any) => t.value === driverTemplateId);
  const isContimoWidget = selectedTemplate?.renderMode === 'CONTIMO_WIDGET';

  const { fields, append, remove } = useFieldArray({
    control,
    name: "templateSwitchers"
  });

  return (
    <>
      <HiddenInput htmlfor="publisherId" />

      <SingleLineField
        top
        label="Key"
        htmlfor="key"
        required
      />
      <DropdownField
        label="Website"
        options={optionsData?.websites.items}
        htmlfor="websiteId"
        required
      />
      <DropdownField
        label="Templates"
        options={optionsData?.templates.items}
        htmlfor="driverTemplateId"
        required
      />
      <SingleLineField
        label="Name"
        htmlfor="name"
        required
      />

      {isContimoWidget && (
        <FormItem label="Template Switchers">
          <div className="space-y-4">
            {fields.map((field, index) => (
              <div key={field.id} className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Teasers count</label>
                  <Controller
                    name={`templateSwitchers.${index}.teasers`}
                    control={control}
                    rules={{ required: true, min: 1 }}
                    render={({ field: { onChange, value } }) => (
                      <InputNumber
                        className="w-full"
                        size="large"
                        min={1}
                        value={value}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Template</label>
                  <BasicDropdownField
                    htmlfor={`templateSwitchers.${index}.templateId`}
                    options={optionsData?.templates.items}
                    required
                  />
                </div>
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="mb-2 p-2 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => append({ teasers: 1, templateId: undefined })}
              className="flex items-center text-sm text-blue-600 hover:text-blue-800"
            >
              <PlusIcon className="h-4 w-4 mr-1" /> Add Template Switcher
            </button>
          </div>
        </FormItem>
      )}
    </>
  );
};

const getColumns = (templates: {value: string, label: string}[], actions?: {onEdit: ActionHandler<Placement>, onGetTags: ActionHandler<Placement>}): ColumnsType<Placement> => [
  {
    key: "key",
    title: "Key",
    dataIndex: "key",
  },
  {
    key: "name",
    title: "Name",
    dataIndex: "name",
  },
  {
    key: "website",
    title: "Website",
    dataIndex: ["website", "name"],
  },
  {
    key: "template",
    title: "Template",
    dataIndex: "driverTemplateId",
    render: (driverTemplateId) => templates.find(({value}) => value === driverTemplateId)?.label
  },
  ...actions ? [{
    key: "actions",
    title: "",
    render: (_, row) => <div className="px-3 flex gap-1">
      <ActionButton row={row} onClick={actions.onGetTags} Icon={CodeBracketIcon} tooltip="Get tags" />
      <ActionButton row={row} onClick={actions.onEdit} Icon={PencilIcon} tooltip="Edit" />
    </div>,
  }] as ColumnsType<Placement> : []

];

interface PlacementsFormProps {
  className?: string;
}

const PlacementsForm: React.FC<PlacementsFormProps> = ({
  className,
}) => {
  const {user, isAdmin} = useCurrentUser()
  const [placement, setPlacement] = useState<Placement>();
  const [modalVisible, showModal, hideModal] = useModal()
  const [tagsPlacement, setTagsPlacement] = useState<Placement>();
  const [tagsModalVisible, showTagsModal, hideTagsModal] = useModal()

  const {variables, setPagination, setFilters} = useQueryVariables()
  const { loading: placementsLoading, error, data } = useQuery(GET_PLACEMENTS, {
    variables
  });
  const {loading: optionsLoading, data: optionsData} = useQuery(GET_ALL_WEBSITES_AND_TEMPLATES)

  const [createPlacement] = useMutation(CREATE_PLACEMENT, {
    refetchQueries: ['PlacementMany']
  })
  const [updatePlacement] = useMutation(UPDATE_PLACEMENT)

  const handleOk = async (record: CreatePlacementInput | Placement) => {
    const cleanRecord = { ...record } as any;
    if (cleanRecord.templateSwitchers) {
      cleanRecord.templateSwitchers = cleanRecord.templateSwitchers.map((s: any) => ({
        teasers: s.teasers,
        templateId: s.templateId
      }));
    }

    if (placement) {
      const { _id, __typename, website, code, websiteId, publisherId, ...updatedRecord } = cleanRecord
      await updatePlacement({ variables: { id: placement._id, record: updatedRecord } })
    } else {
      await createPlacement({
        variables: { record: cleanRecord }
      })
    }
  }

  const handleEditPlacement = (placement: Placement) => {
    setPlacement(placement)
    showModal()
  }

  const handleGetTags = (placement: Placement) => {
    setTagsPlacement(placement)
    showTagsModal()
  }

  const loading = placementsLoading || optionsLoading
  const tableProps = isAdmin
  ? {
    actionButtonText: "Add Placement",
    onActionButtonClick: showModal,
    columns: getColumns(optionsData?.templates.items, {onEdit: handleEditPlacement, onGetTags: handleGetTags})
  }
  : {
    columns: getColumns(optionsData?.templates.items)
  }

  return (
    <div className={className}>
      <Table
        data={loading ? [] : data.placements.items}
        selectable={false}
        pagination={{
          total: data ? data.placements.meta.totalItems : 0,
          pageSize: variables.perPage,
          current: variables.page
        }}
        onChange={(newPagination, newFilters) => {
          setPagination(newPagination)
          setFilters(newFilters)
        }}
        {...tableProps}
        loading={loading}
      />

      <FormModal
        open={modalVisible}
        title={`${placement ? 'Edit' : 'Add'} Placement`}
        okText={placement ? 'Save' : 'Add'}
        defaultValues={placement || { publisherId: user.companyId }}
        onCancel={() => {
          hideModal()
          setPlacement(undefined)
        }}
        onOk={handleOk}
      >
        <PlacementFormContent optionsData={optionsData} />

      </FormModal>

      <GetTagsModal
        open={tagsModalVisible}
        onCancel={() => {
          hideTagsModal()
          setTagsPlacement(undefined)
        }}
        placement={tagsPlacement}
      />
    </div>
  );
};

export default PlacementsForm;
